import { useCallback, useEffect, useMemo, useState } from 'react'

import { faCalendar } from '@fortawesome/pro-regular-svg-icons'
import dayjs, { Dayjs } from 'dayjs'

import { useCurrentBreakpoint } from 'src/hooks/use-current-breakpoint'
import { DateFormats } from 'src/utils/date'

import Calendar from '../Calendar/Calendar'
import { formatDateForInputText, getIsStringCompleteDate } from '../DatePicker/util'
import Flex from '../Flex/Flex'
import HStack from '../Flex/HStack'
import VStack from '../Flex/VStack'
import Input from '../Input/Input'

import { DateRangePickerValue, DateRangeType } from './DateRangePicker'

type Props = {
  endDate?: Dayjs
  dateFormat: DateFormats
  startDate?: Dayjs

  onChange: (value: DateRangePickerValue) => void
  onClose: () => void
}

export const CustomDateRangePopover = ({ startDate, endDate, dateFormat, onChange }: Props) => {
  const formattedStartDate = formatDateForInputText(startDate, { format: dateFormat })
  const formattedEndDate = formatDateForInputText(endDate, { format: dateFormat })

  const [startDateInput, setStartDateInput] = useState<string>(formattedStartDate)
  const [endDateInput, setEndDateInput] = useState<string>(formattedEndDate)

  const { isSmallScreen } = useCurrentBreakpoint()
  const numberOfMonths = isSmallScreen() ? 1 : 2

  const defaultMonth = useMemo(() => {
    if (startDate) {
      return startDate
    }
    if (endDate) {
      return numberOfMonths === 2 ? endDate.subtract(1, 'month') : endDate
    }
    return numberOfMonths === 2 ? dayjs().subtract(1, 'month') : dayjs()
  }, [startDate, endDate, numberOfMonths])

  const [month, setMonth] = useState(defaultMonth)
  const selectedMonth = useMemo(() => month.toDate(), [month])

  useEffect(() => {
    setMonth(defaultMonth)
  }, [defaultMonth])

  const handleMonthChanged = useCallback((newValue: Date | undefined) => {
    setMonth(newValue ? dayjs(newValue) : dayjs())
  }, [])

  const resetInputText = useCallback(() => {
    setStartDateInput(formattedStartDate)
    setEndDateInput(formattedEndDate)
  }, [formattedEndDate, formattedStartDate])

  const handleSubmit = useCallback(
    (
      newStartDate: Dayjs | undefined,
      newEndDate: Dayjs | undefined,
      shouldResetInputText: boolean
    ) => {
      const isValid =
        newStartDate &&
        newEndDate &&
        (newStartDate.isBefore(newEndDate) || newStartDate.isSame(newEndDate))

      if (!isValid) {
        return
      }

      onChange({ type: DateRangeType.Custom, startDate: newStartDate, endDate: newEndDate })
      if (shouldResetInputText) {
        setStartDateInput(formatDateForInputText(newStartDate, { format: dateFormat }))
        setEndDateInput(formatDateForInputText(newEndDate, { format: dateFormat }))
      }
    },
    [dateFormat, onChange]
  )

  const onStartDateChange = useCallback(
    (newValue: string | undefined) => {
      if (!newValue) {
        setStartDateInput('')
        return
      }
      setStartDateInput(newValue)

      if (getIsStringCompleteDate(newValue)) {
        handleSubmit(dayjs(newValue), endDate, false)
      }
    },
    [handleSubmit, endDate]
  )

  const onEndDateChange = useCallback(
    (newValue: string | undefined) => {
      if (!newValue) {
        setEndDateInput('')
        return
      }
      setEndDateInput(newValue)

      if (getIsStringCompleteDate(newValue)) {
        // don't reset the input text as the user is still typing
        handleSubmit(startDate, dayjs(newValue), false)
      }
    },
    [handleSubmit, startDate]
  )

  const onStartDateBlur = useCallback(() => {
    if (!startDateInput && endDate) {
      // If the start date is empty, set it to the end date
      handleSubmit(endDate, endDate, true)
    } else if (startDateInput && endDate && dayjs(startDateInput).isAfter(endDate)) {
      // if the new start date is after the end date, set the end date to the new start date
      const newStartDate = dayjs(startDateInput)
      handleSubmit(newStartDate, newStartDate, true)
    } else {
      resetInputText()
    }
  }, [startDateInput, endDate, handleSubmit, resetInputText])

  const onEndDateBlur = useCallback(() => {
    if (!endDateInput && startDate) {
      // If the end date is empty, set it to the start date
      handleSubmit(startDate, startDate, true)
    } else if (endDateInput && startDate && dayjs(endDateInput).isBefore(startDate)) {
      // if the new end date is before the start date, set the start date to the new end date
      const newEndDate = dayjs(endDateInput)
      handleSubmit(newEndDate, newEndDate, true)
    } else {
      resetInputText()
    }
  }, [endDateInput, startDate, handleSubmit, resetInputText])

  const selectedRange = useMemo(() => {
    const toLocalDateOnly = (d?: Dayjs): Date | undefined => {
      // This is a hack where the day picker only supports local dates,
      // so we need to ensure that the dates are in the local time zone
      if (!d) {
        return undefined
      }

      const dateYear = d.year()
      const dateMonth = d.month() // zero-based
      const dateDay = d.date()

      return new Date(dateYear, dateMonth, dateDay) // → midnight in local system time zone
    }

    return {
      from: toLocalDateOnly(startDate),
      to: toLocalDateOnly(endDate),
    }
  }, [startDate, endDate])

  const handleRangeSelect = useCallback(
    (range: { from?: Date; to?: Date } | undefined) => {
      if (!range) {
        return
      }
      const { from, to } = range
      const newFrom = from ? dayjs(from) : startDate
      const newTo = to ? dayjs(to) : endDate

      handleSubmit(newFrom, newTo, true)
    },
    [endDate, startDate, handleSubmit]
  )

  const inputDirection = isSmallScreen() ? 'column' : 'row'

  return (
    <VStack gap={6}>
      <Flex gap={3} direction={inputDirection}>
        <Input
          label="Start date"
          value={startDateInput}
          onValueChange={onStartDateChange}
          onBlur={onStartDateBlur}
          suffixIcon={faCalendar}
        />
        <Input
          label="End date"
          value={endDateInput}
          onValueChange={onEndDateChange}
          onBlur={onEndDateBlur}
          suffixIcon={faCalendar}
        />
      </Flex>
      <HStack align="center" justify="center">
        <Calendar
          mode="range"
          selected={selectedRange}
          month={selectedMonth}
          onMonthChange={handleMonthChanged}
          onSelect={handleRangeSelect}
          numberOfMonths={numberOfMonths}
        />
      </HStack>
    </VStack>
  )
}
